import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Alert, Button, Layout, Menu, Select, Space } from 'antd';
import { UserOutlined } from '@ant-design/icons';

import { EmptyHeader, LoadingScreen } from 'components';
import { logout, selectors } from 'state';

import {
  ActivityOverview,
  ObservedKlass,
  fetchActivityOverview,
  fetchObservedKlasses,
} from './api';
// The Trainees / Node matrix tab panes are hidden (2026-08-26): the Studio is
// the client's single view. RosterTable/NodeMatrix/TraineeDrawer and the
// activity_matrix endpoint remain intact — restore the Tabs from git history.
import StudioView from './StudioView';

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/** Dev helper: the resolved plan id rides the URL (/dashboard/<plan-id>).
 *  Hand-editing it (and reloading) overrides the plan via the API's ?plan=
 *  (which accepts an id or a code slug). */
const planFromUrl = () => {
  const match = window.location.pathname.match(/^\/dashboard\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
};

const ClientDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const username = useSelector(selectors.username);

  const [klasses, setKlasses] = useState<ObservedKlass[]>([]);
  const [klassId, setKlassId] = useState<number>();
  // Read once at mount; history.replace below never re-triggers a fetch.
  const [plan] = useState<string | undefined>(planFromUrl);
  const [overview, setOverview] = useState<ActivityOverview>();
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    let active = true;
    fetchObservedKlasses()
      .then(result => {
        if (!active) return;
        setKlasses(result);
        setKlassId(current => current ?? result[0]?.id);
        if (!result.length) {
          setWaiting(false);
          setError('No class is assigned to this account yet.');
        }
      })
      .catch(requestError => {
        if (!active) return;
        setWaiting(false);
        setError(errorMessage(requestError, 'Your classes could not be loaded.'));
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (klassId === undefined) return;
    let active = true;
    setWaiting(true);
    setError(undefined);

    fetchActivityOverview(klassId, plan)
      .then(overviewResult => {
        if (active) setOverview(overviewResult);
      })
      .catch(requestError => {
        if (active)
          setError(
            errorMessage(requestError, 'The activity data could not be loaded.'),
          );
      })
      .finally(() => {
        if (active) setWaiting(false);
      });

    return () => {
      active = false;
    };
  }, [klassId, plan, reloadVersion]);

  // Kept only for the error Alert's Retry action (no toolbar Refresh button).
  const retry = useCallback(() => setReloadVersion(v => v + 1), []);

  // Reflect the RESOLVED plan id in the URL (replace, not push — no history
  // spam). The plan state deliberately does not track this, so no refetch.
  const resolvedPlanId = overview?.plan?.id;
  useEffect(() => {
    if (!resolvedPlanId) return;
    const target = `/dashboard/${resolvedPlanId}`;
    if (window.location.pathname !== target) history.replace(target);
  }, [resolvedPlanId, history]);

  const header = (
    <EmptyHeader defaultKey="dashboard">
      <Menu.Item key="dashboard">Activity Dashboard</Menu.Item>
      <Menu.SubMenu
        title={
          <>
            <UserOutlined />
            {username}
          </>
        }
        style={{ marginLeft: 'auto' }}
      >
        <Menu.Item onClick={() => dispatch(logout({}))}>Logout</Menu.Item>
      </Menu.SubMenu>
    </EmptyHeader>
  );

  if (waiting && !overview)
    return (
      <Layout style={{ minHeight: '100vh' }}>
        {header}
        <LoadingScreen />
      </Layout>
    );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {header}
      <Layout.Content style={{ padding: 24 }}>
        {/* Klass picker only when the observer watches several klasses (the
            klass name itself lives in the Studio's roster header). No plan
            picker: the backend resolves the latest visible plan; the resolved
            code is reflected in the URL as a subtle dev helper/override. */}
        {klasses.length > 1 && (
          <Space style={{ marginBottom: 16 }} wrap>
            <Select
              value={klassId}
              style={{ minWidth: 220 }}
              onChange={value => setKlassId(value)}
              options={klasses.map(klass => ({
                value: klass.id,
                label: klass.name,
              }))}
            />
          </Space>
        )}

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={retry}>
                Retry
              </Button>
            }
          />
        )}

        {overview && klassId !== undefined && (
          <StudioView
            overview={overview}
            klassId={klassId}
            plan={plan ?? overview.plan?.code}
          />
        )}
      </Layout.Content>
    </Layout>
  );
};

export default ClientDashboard;
